// ============================================================================
// ROLES ADMIN - /admin/roles (Admin::ManageRoles)
// Create roles, tick their global permissions and the roles they may assign.
// The backend enforces every rule (authz/role-admin.service.ts); this page
// shows its messages as they come.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, KeyRound, Loader2, Lock, Plus, RefreshCw, Save, Search, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  CatalogueAction,
  Role,
  RoleDraft,
  RoleGroup,
  RoleHolder,
  ROLE_GROUP_LABELS,
  ROLE_GROUP_ORDER,
  SYSTEM_ADMINISTRATOR_ROLE_ID,
  apiErrorMessage,
  createRole,
  deleteRole,
  getCatalogue,
  listRoleHolders,
  listRoles,
  updateRole,
} from '../../services/roles';

const inputClass = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none disabled:bg-slate-50 disabled:text-slate-400';
const labelClass = 'block text-sm font-semibold text-navy mb-2';
const sectionLabel = 'text-[11px] font-bold text-primary tracking-widest uppercase';
const secondaryButton = 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors';
const primaryButton = 'flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60';

const NEW = '__new__';
const emptyDraft: RoleDraft = { name: '', parentId: null, actions: [], assignableRoleIds: [] };

function draftOf(role: Role): RoleDraft {
  return { name: role.name, parentId: role.parentId ?? null, actions: [...role.actions], assignableRoleIds: [...role.assignableRoleIds] };
}

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function symmetricDiff(a: string[], b: string[]): number {
  const sa = new Set(a);
  const sb = new Set(b);
  return a.filter((x) => !sb.has(x)).length + b.filter((x) => !sa.has(x)).length;
}

// "Integrations: Teams relay" reads as "Teams relay" under the Integrations heading.
function shortName(role: Role): string {
  const prefix = `${ROLE_GROUP_LABELS[role.group]}: `;
  return role.name.startsWith(prefix) ? role.name.slice(prefix.length) : role.name;
}

// Nearest ancestor name for every id granted up the parent chain.
function inheritedFrom(parentId: string | null | undefined, byId: Map<string, Role>, pick: (r: Role) => string[]): Map<string, string> {
  const out = new Map<string, string>();
  const seen = new Set<string>();
  let id = parentId ?? undefined;
  while (id && !seen.has(id)) {
    seen.add(id);
    const r = byId.get(id);
    if (!r) break;
    for (const x of pick(r)) if (!out.has(x)) out.set(x, r.name);
    id = r.parentId;
  }
  return out;
}

const Badge: React.FC<{ tone: 'slate' | 'amber' | 'primary'; children: React.ReactNode; title?: string }> = ({ tone, children, title }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-500 border-slate-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    primary: 'bg-primary/10 text-primary border-primary/20',
  };
  return <span title={title} className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${tones[tone]}`}>{children}</span>;
};

const Tick: React.FC<{
  label: string;
  checked: boolean;
  disabled: boolean;
  via?: string;
  title?: string;
  onChange: () => void;
}> = ({ label, checked, disabled, via, title, onChange }) => (
  <label title={title} className={`flex items-start gap-2 text-sm py-1 ${disabled ? 'text-slate-400' : 'text-slate-700 cursor-pointer'}`}>
    <input type="checkbox" className="mt-0.5 accent-[#00c7c4] flex-shrink-0" checked={checked} disabled={disabled} onChange={onChange} />
    <span>
      {label}
      {via && <span className="ml-2 text-[11px] font-semibold text-primary/80 whitespace-nowrap">via {via}</span>}
    </span>
  </label>
);

const AreaCard: React.FC<{
  title: string;
  ticked: number;
  total: number;
  onAll?: () => void;
  allTicked: boolean;
  children: React.ReactNode;
}> = ({ title, ticked, total, onAll, allTicked, children }) => (
  <div className="border border-[#e2e8f0] rounded-xl bg-white">
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#e2e8f0] bg-slate-50/50 rounded-t-xl">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-navy">{title}</span>
        <span className={`text-xs tabular-nums ${ticked ? 'text-primary font-semibold' : 'text-slate-400'}`}>{ticked} of {total}</span>
      </div>
      {onAll && (
        <button type="button" onClick={onAll} className="text-xs font-bold text-slate-500 hover:text-primary">
          {allTicked ? 'Clear' : 'Tick all'}
        </button>
      )}
    </div>
    <div className="px-4 py-2">{children}</div>
  </div>
);

export default function RolesAdmin() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalogue, setCatalogue] = useState<CatalogueAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RoleDraft>(emptyDraft);
  const [holders, setHolders] = useState<RoleHolder[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [query, setQuery] = useState('');
  const [tickedOnly, setTickedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [r, c] = await Promise.all([listRoles(), getCatalogue()]);
      setRoles(r);
      setCatalogue(c);
      return r;
    } catch (e: any) {
      setLoadError(apiErrorMessage(e, 'Failed to load roles'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);
  const selected = selectedId && selectedId !== NEW ? byId.get(selectedId) ?? null : null;
  const isNew = selectedId === NEW;
  const readOnly = selected?.id === SYSTEM_ADMINISTRATOR_ROLE_ID;

  const select = (id: string | null, list: Role[] = roles) => {
    setSelectedId(id);
    setError(null);
    setConflict(false);
    setConfirmDelete(false);
    setHolders(null);
    const role = id && id !== NEW ? list.find((r) => r.id === id) : null;
    setDraft(role ? draftOf(role) : emptyDraft);
    if (role) listRoleHolders(role.id).then(setHolders).catch(() => setHolders([]));
  };

  const reload = async () => {
    const fresh = await load();
    if (fresh) select(selectedId && fresh.some((r) => r.id === selectedId) ? selectedId : null, fresh);
  };

  const actionsByArea = useMemo(() => {
    const areas = new Map<string, CatalogueAction[]>();
    for (const a of catalogue) areas.set(a.area, [...(areas.get(a.area) ?? []), a]);
    return [...areas.entries()];
  }, [catalogue]);

  const listGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ROLE_GROUP_ORDER
      .map((g) => [g, roles.filter((r) => r.group === g && (!q || r.name.toLowerCase().includes(q)))] as [RoleGroup, Role[]])
      .filter(([, list]) => list.length > 0);
  }, [roles, query]);

  const assignableGroups = useMemo(() => ROLE_GROUP_ORDER
    .map((g) => [g, roles.filter((r) => r.group === g && r.id !== SYSTEM_ADMINISTRATOR_ROLE_ID)] as const)
    .filter(([, list]) => list.length > 0), [roles]);

  const inheritedActions = useMemo(() => inheritedFrom(draft.parentId, byId, (r) => r.actions), [draft.parentId, byId]);
  const inheritedAssign = useMemo(() => inheritedFrom(draft.parentId, byId, (r) => r.assignableRoleIds), [draft.parentId, byId]);

  const hasAction = (id: string) => readOnly || draft.actions.includes(id) || inheritedActions.has(id);
  const canAssign = (id: string) => readOnly || draft.assignableRoleIds.includes(id) || inheritedAssign.has(id);
  const effectiveCount = readOnly ? catalogue.length : catalogue.filter((a) => hasAction(a.id)).length;

  const fail = (e: any, fallback: string) => {
    if (e?.response?.status === 409 && /changed since/i.test(e?.response?.data?.error ?? '')) setConflict(true);
    setError(apiErrorMessage(e, fallback));
  };

  const save = async () => {
    if (!draft.name.trim()) { setError('Give the role a name'); return; }
    setSaving(true);
    setError(null);
    setConflict(false);
    try {
      const body = { ...draft, name: draft.name.trim() };
      const saved = isNew ? await createRole(body) : await updateRole(selected!.id, body, selected!.version);
      toast.success(isNew ? `Created ${saved.name}` : `Saved ${saved.name}`);
      const fresh = await load();
      if (fresh) select(saved.id, fresh);
    } catch (e: any) {
      fail(e, 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await deleteRole(selected.id, selected.version);
      toast.success(`Deleted ${selected.name}`);
      const fresh = await load();
      if (fresh) select(null, fresh);
    } catch (e: any) {
      fail(e, 'Failed to delete role');
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const changes = isNew ? 0 : selected
    ? (draft.name !== selected.name ? 1 : 0)
      + ((draft.parentId ?? null) !== (selected.parentId ?? null) ? 1 : 0)
      + symmetricDiff(draft.actions, selected.actions)
      + symmetricDiff(draft.assignableRoleIds, selected.assignableRoleIds)
    : 0;
  const dirty = isNew || changes > 0;

  const tickArea = (actions: CatalogueAction[]) => {
    const editable = actions.filter((a) => !a.systemAdministratorOnly && !inheritedActions.has(a.id)).map((a) => a.id);
    const all = editable.every((id) => draft.actions.includes(id));
    setDraft({ ...draft, actions: all ? draft.actions.filter((id) => !editable.includes(id)) : [...new Set([...draft.actions, ...editable])] });
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[2200px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <KeyRound size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Roles</h1>
              <p className="text-slate-500 text-sm font-medium">
                {roles.length} roles · changes apply to every holder within a few seconds
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => select(NEW)} className={primaryButton}>
              <Plus size={16} /> New role
            </button>
            <button onClick={reload} title="Reload" className={secondaryButton}>
              <RefreshCw size={16} />
            </button>
          </div>
        </header>

        {loadError && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-700">{loadError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6 items-start">
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden lg:sticky lg:top-0 flex flex-col lg:max-h-[calc(100vh-9rem)]">
            <div className="p-3 border-b border-[#e2e8f0]">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a role"
                  className="w-full border border-[#e2e8f0] rounded-xl pl-8 pr-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
            </div>
            {loading && !roles.length ? (
              <div className="flex justify-center items-center py-20 text-[#64748b]"><Loader2 className="animate-spin" size={20} /></div>
            ) : (
              <div className="overflow-y-auto py-1">
                {listGroups.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 text-center">No roles match.</p>}
                {listGroups.map(([group, list]) => (
                  <div key={group} className="py-1">
                    <p className="px-4 pt-2 pb-1 text-[11px] font-bold text-slate-400 tracking-widest uppercase">{ROLE_GROUP_LABELS[group]}</p>
                    {list.map((r) => {
                      const active = selectedId === r.id;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => select(r.id)}
                          className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-left border-l-2 transition-colors ${active ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-slate-50'}`}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-sm truncate ${active ? 'font-semibold text-navy' : 'font-medium text-slate-700'}`}>{shortName(r)}</span>
                              {r.id === SYSTEM_ADMINISTRATOR_ROLE_ID && <Lock size={11} className="text-slate-400 flex-shrink-0" />}
                              {r.editedSinceSeed && <Badge tone="amber" title="An admin has changed this seeded role, so the seed no longer updates it">Edited</Badge>}
                            </div>
                            {r.parentId && <p className="text-[11px] text-slate-400 truncate">Inherits {byId.get(r.parentId)?.name ?? 'a deleted role'}</p>}
                          </div>
                          <span
                            title={`${r.holders} holder${r.holders === 1 ? '' : 's'}`}
                            className={`flex-shrink-0 min-w-[1.75rem] text-center px-1.5 py-0.5 rounded-full text-[11px] font-semibold tabular-nums ${r.holders ? 'bg-slate-100 text-slate-600' : 'text-slate-300'}`}
                          >
                            {r.holders}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedId ? (
            <div className="bg-white border border-[#e2e8f0] rounded-2xl">
              <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-navy">{isNew ? 'New role' : selected?.name}</h2>
                  {selected && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      Version {selected.version} · {selected.seeded ? 'seeded' : 'custom'} · {selected.holders} holder{selected.holders === 1 ? '' : 's'} · {effectiveCount} of {catalogue.length} permissions
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {selected?.editedSinceSeed && <Badge tone="amber">Edited since seed</Badge>}
                  {selected?.seeded && <Badge tone="slate">Seeded</Badge>}
                </div>
              </div>

              {error && (
                <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3 items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-red-700 flex-1">{error}</p>
                  {conflict && (
                    <button onClick={reload} className="text-sm font-bold text-red-700 underline whitespace-nowrap">Reload roles</button>
                  )}
                </div>
              )}

              {readOnly && (
                <div className="px-5 py-4 border-b border-[#e2e8f0] bg-slate-50 flex gap-3">
                  <Lock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-600">System Administrator always holds every permission and can assign every role. It cannot be edited.</p>
                </div>
              )}

              <div className="p-5 border-b border-[#e2e8f0] grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Name</label>
                  <input className={inputClass} value={draft.name} disabled={readOnly} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Reporting" />
                </div>
                <div>
                  <label className={labelClass}>Inherits from</label>
                  <select
                    className={`${inputClass} bg-white`}
                    value={draft.parentId ?? ''}
                    disabled={readOnly}
                    onChange={(e) => setDraft({ ...draft, parentId: e.target.value || null })}
                  >
                    <option value="">Nothing</option>
                    {roles.filter((r) => r.id !== selected?.id && r.id !== SYSTEM_ADMINISTRATOR_ROLE_ID).map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-400">Holders also get every permission of the parent.</p>
                </div>
              </div>

              <div className="p-5 border-b border-[#e2e8f0]">
                <div className="flex items-center justify-between mb-4">
                  <span className={sectionLabel}>Permissions</span>
                  <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                    {([['All', false], ['Ticked only', true]] as const).map(([label, value]) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setTickedOnly(value)}
                        className={`px-3 py-1.5 text-xs font-semibold transition-all ${tickedOnly === value ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="columns-1 xl:columns-2 2xl:columns-3 gap-3 [&>*]:break-inside-avoid [&>*]:mb-3">
                  {actionsByArea.map(([area, actions]) => {
                    const open = actions.filter((a) => !a.systemAdministratorOnly);
                    const sysOnly = actions.filter((a) => a.systemAdministratorOnly);
                    const ticked = actions.filter((a) => hasAction(a.id)).length;
                    const shown = tickedOnly ? open.filter((a) => hasAction(a.id)) : open;
                    if (tickedOnly && !ticked) return null;
                    const editable = open.filter((a) => !inheritedActions.has(a.id));
                    return (
                      <AreaCard
                        key={area}
                        title={area}
                        ticked={ticked}
                        total={actions.length}
                        allTicked={editable.every((a) => draft.actions.includes(a.id))}
                        onAll={readOnly || !editable.length ? undefined : () => tickArea(actions)}
                      >
                        {shown.map((a) => {
                          const via = !readOnly && !draft.actions.includes(a.id) ? inheritedActions.get(a.id) : undefined;
                          return (
                            <Tick
                              key={a.id}
                              label={a.description}
                              title={a.id}
                              checked={hasAction(a.id)}
                              disabled={readOnly || !!via}
                              via={via}
                              onChange={() => setDraft({ ...draft, actions: toggle(draft.actions, a.id) })}
                            />
                          );
                        })}
                        {sysOnly.length > 0 && (!tickedOnly || readOnly) && (
                          <details className="mt-1 pt-1 border-t border-dashed border-[#e2e8f0]">
                            <summary className="flex items-center gap-1.5 py-1 text-xs text-slate-400 cursor-pointer select-none">
                              <Lock size={11} /> {sysOnly.length} reserved for System Administrator
                            </summary>
                            {sysOnly.map((a) => (
                              <Tick key={a.id} label={a.description} title={a.id} checked={readOnly} disabled onChange={() => undefined} />
                            ))}
                          </details>
                        )}
                      </AreaCard>
                    );
                  })}
                </div>
                {tickedOnly && effectiveCount === 0 && <p className="text-sm text-slate-400">No permissions ticked yet.</p>}
              </div>

              <div className="p-5 border-b border-[#e2e8f0]">
                <div className="flex items-center justify-between mb-4">
                  <span className={sectionLabel}>May assign</span>
                  <span className="text-xs text-slate-400">Roles holders can give to other users</span>
                </div>
                <div className="columns-1 xl:columns-2 2xl:columns-3 gap-3 [&>*]:break-inside-avoid [&>*]:mb-3">
                  {assignableGroups.map(([group, list]) => (
                    <AreaCard key={group} title={ROLE_GROUP_LABELS[group]} ticked={list.filter((r) => canAssign(r.id)).length} total={list.length} allTicked={false}>
                      {list.map((r) => {
                        const via = !readOnly && !draft.assignableRoleIds.includes(r.id) ? inheritedAssign.get(r.id) : undefined;
                        return (
                          <Tick
                            key={r.id}
                            label={shortName(r)}
                            checked={canAssign(r.id)}
                            disabled={readOnly || !!via}
                            via={via}
                            onChange={() => setDraft({ ...draft, assignableRoleIds: toggle(draft.assignableRoleIds, r.id) })}
                          />
                        );
                      })}
                    </AreaCard>
                  ))}
                </div>
              </div>

              {selected && (
                <div className="p-5 border-b border-[#e2e8f0]">
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={14} className="text-slate-400" />
                    <span className={sectionLabel}>Holders</span>
                  </div>
                  {holders === null ? (
                    <Loader2 className="animate-spin text-slate-400" size={16} />
                  ) : holders.length === 0 ? (
                    <p className="text-sm text-slate-400">Nobody holds this role.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {holders.map((h) => (
                        <span
                          key={h.userId}
                          title={`${h.email}${h.status !== 'active' ? ` · ${h.status}` : ''}`}
                          className={`px-3 py-1 rounded-full border text-xs font-medium ${h.status === 'active' ? 'border-[#e2e8f0] bg-slate-50 text-navy' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
                        >
                          {h.displayName || h.email}
                          {h.status !== 'active' && <span className="ml-1 opacity-70">({h.status})</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!readOnly && (
                <div className={`sticky bottom-0 flex items-center justify-between px-5 py-4 border-t rounded-b-2xl transition-colors ${dirty ? 'bg-primary/5 border-primary/30 backdrop-blur' : 'bg-slate-50 border-[#e2e8f0]'}`}>
                  <div>
                    {selected && (confirmDelete ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">Delete {selected.name}?</span>
                        <button onClick={remove} disabled={saving} className="bg-red-600 text-white font-medium text-sm py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-60">Delete</button>
                        <button onClick={() => setConfirmDelete(false)} className="text-sm font-bold text-slate-500 px-2">Keep</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDelete(true)} className={`${secondaryButton} !text-red-600 hover:!bg-red-50`}>
                        <Trash2 size={16} /> Delete
                      </button>
                    ))}
                  </div>
                  {dirty ? (
                    <div className="flex items-center gap-3">
                      {changes > 0 && <span className="text-sm font-semibold text-navy">{changes} unsaved change{changes === 1 ? '' : 's'}</span>}
                      <button onClick={() => select(isNew ? null : selectedId)} className={secondaryButton}>
                        {isNew ? 'Cancel' : 'Discard'}
                      </button>
                      <button onClick={save} disabled={saving} className={primaryButton}>
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {isNew ? 'Create role' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => select(null)} className={secondaryButton}>Close</button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <KeyRound size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">Select a role to see and change its permissions.</p>
              <p className="text-slate-400 text-xs mt-2">Seeded roles you edit stop receiving new permissions from the seed.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
